import React, { useState, useEffect } from 'react';
import { FiFilter, FiDownload, FiShoppingBag, FiPackage, FiClock, FiCheckCircle, FiXCircle, FiUser, FiPhone, FiMapPin, FiRefreshCw } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, [filterStatus, filterType]);

  const fetchOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('orderType', filterType);
      
      const response = await api.get(`/vendor-owner/sales/consolidated?${params.toString()}`);
      if (response.data.success) {
        setOrders(response.data.data.recentOrders || []);
        toast.success('Orders data refreshed successfully!');
      }
    } catch (error) {
      toast.error('Failed to fetch orders');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#ff9800',
      accepted: '#2196f3',
      preparing: '#9c27b0',
      ready: '#00bcd4',
      completed: '#4caf50',
      cancelled: '#f44336'
    };
    return colors[status] || '#9e9e9e';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <FiShoppingBag className="text-indigo-600" />
              All Orders
            </h1>
            <p className="text-gray-600">Comprehensive order management across all restaurants</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={fetchOrders}
            >
              <FiRefreshCw size={16} />
              Refresh
            </button>
            <button 
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
              onClick={() => {
                // Export functionality can be added here
                toast.info('Export functionality coming soon!');
              }}
            >
              <FiDownload size={16} />
              Export
            </button>
          </div>
        </div>
        
        {/* Stats */}
        <div className="bg-indigo-50 rounded-xl px-4 py-2 mt-4 inline-flex">
          <span className="text-2xl font-bold text-indigo-600">{orders.length}</span>
          <span className="text-sm text-indigo-700 ml-2">Total Orders</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-600" size={20} />
            <span className="font-medium text-gray-700">Filters:</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Status</label>
              <select 
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Type</label>
              <select 
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="dine-in">Dine In</option>
                <option value="pickup">Pickup</option>
                <option value="delivery">Delivery</option>
                <option value="vehicle">Vehicle</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiShoppingBag className="text-gray-400 text-3xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">No Orders Found</h3>
          <p className="text-gray-600">No orders match your current filters. Try adjusting the filters above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {orders.map((order) => (
            <div key={order._id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-200 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className="inline-flex px-3 py-1 bg-white/20 rounded-full text-white font-mono text-sm font-medium">
                      #{order.orderNumber}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                      order.orderType === 'dine-in' ? 'bg-blue-100 text-blue-800' :
                      order.orderType === 'pickup' ? 'bg-green-100 text-green-800' :
                      order.orderType === 'delivery' ? 'bg-orange-100 text-orange-800' :
                      order.orderType === 'vehicle' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      <FiPackage size={12} />
                      {order.orderType || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <FiClock size={14} />
                  {new Date(order.createdAt).toLocaleString('en-IN', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <FiMapPin className="text-indigo-600 text-sm" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Restaurant</div>
                      <div className="text-sm font-medium text-gray-900">{order.vendorId?.restaurantDetails?.restaurantName || 'N/A'}</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <FiUser className="text-green-600 text-sm" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Customer</div>
                      <div className="text-sm font-medium text-gray-900">{order.customerId?.fullName || 'Guest'}</div>
                    </div>
                  </div>

                  {order.customerId?.mobileNumber && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FiPhone className="text-blue-600 text-sm" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Phone</div>
                        <div className="text-sm font-medium text-gray-900">{order.customerId.mobileNumber}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Card Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-500">Total Amount</div>
                    <div className="text-xl font-bold text-gray-900">{formatCurrency(order.totalRevenue)}</div>
                  </div>
                  <span 
                    className="inline-flex px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {order.status === 'completed' && <FiCheckCircle size={12} />}
                    {order.status === 'cancelled' && <FiXCircle size={12} />}
                    {(order.status === 'pending' || order.status === 'accepted' || order.status === 'preparing') && <FiClock size={12} />}
                    {order.status === 'ready' && <FiCheckCircle size={12} />}
                    {order.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllOrders;
